import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';

interface JobPostConfigHeaderProps {
  completedStepsCount: number;
  totalStepsCount: number;
  progressPercent: number;
}

export const JobPostConfigHeader = ({
  completedStepsCount,
  totalStepsCount,
  progressPercent,
}: JobPostConfigHeaderProps) => {
  const isAllComplete =
    totalStepsCount > 0 && completedStepsCount === totalStepsCount;

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: '16px',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: isAllComplete ? 'success.light' : 'divider',
        background: isAllComplete
          ? 'linear-gradient(135deg, rgba(236,253,245,0.8) 0%, rgba(255,255,255,0.9) 100%)'
          : 'linear-gradient(135deg, rgba(248,250,252,0.9) 0%, rgba(255,255,255,0.95) 100%)',
        backdropFilter: 'blur(8px)',
        boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.05)',
        transition: 'all 0.3s ease-in-out',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: '10px',
              bgcolor: isAllComplete ? 'success.main' : 'primary.main',
              color: '#ffffff',
              boxShadow: isAllComplete
                ? '0 4px 12px rgba(16, 185, 129, 0.25)'
                : '0 4px 12px rgba(99, 102, 241, 0.25)',
            }}
          >
            {isAllComplete ? (
              <CheckCircleRoundedIcon sx={{ fontSize: 22 }} />
            ) : (
              <AssignmentTurnedInRoundedIcon sx={{ fontSize: 22 }} />
            )}
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}
            >
              Job Posting Configuration
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 500 }}
            >
              Complete all mandatory configuration steps before publishing
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            icon={
              isAllComplete ? (
                <CheckCircleRoundedIcon sx={{ fontSize: '14px !important' }} />
              ) : undefined
            }
            label={
              isAllComplete
                ? 'Ready to Publish'
                : `${completedStepsCount} of ${totalStepsCount} completed`
            }
            color={isAllComplete ? 'success' : 'primary'}
            variant={isAllComplete ? 'filled' : 'outlined'}
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              borderRadius: '8px',
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: isAllComplete ? 'success.dark' : 'primary.main',
              minWidth: 36,
              textAlign: 'right',
            }}
          >
            {progressPercent}%
          </Typography>
        </Box>
      </Box>

      {/* Progress Bar */}
      <Box sx={{ width: '100%', mt: 0.5 }}>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              bgcolor: isAllComplete ? 'success.main' : 'primary.main',
              transition: 'transform 0.4s ease-in-out',
            },
          }}
        />
      </Box>
    </Box>
  );
};
